import { useQuery } from "@tanstack/react-query"
import {
  AssignVendorSkusReq,
  AssignVendorSkusRes,
  BulkUpsertVendorsReq,
  BulkUpsertVendorsRes,
  CreateVendorContactReq,
  CreateVendorContactRes,
  CreateVendorReq,
  CreateVendorRes,
  CreateVendorWithBankAccountReq,
  CreateVendorWithBankAccountRes,
  DeactivateVendorContactReq,
  DeactivateVendorContactRes,
  DeleteVendorBankPaymentMethodReq,
  DeleteVendorBankPaymentMethodRes,
  DuplicateVendorReq,
  DuplicateVendorRes,
  GetVendorByIntegrationReq,
  GetVendorByIntegrationRes,
  GetVendorReq,
  GetVendorRes,
  ListCatalogVendorIntegrationOptionsReq,
  ListCatalogVendorIntegrationOptionsRes,
  ListVendorLocationSettingsOptionsReq,
  ListVendorLocationSettingsOptionsRes,
  ListVendorIntegrationOptionsReq,
  ListVendorIntegrationOptionsRes,
  ListVendorsForCompanyReq,
  ListVendorsForCompanyRes,
  SaveVendorBankPaymentMethodReq,
  SaveVendorBankPaymentMethodRes,
  UpdateVendorAccountsByLocationReq,
  UpdateVendorAccountsByLocationRes,
  UpdateVendorLocationSettingsReq,
  UpdateVendorLocationSettingsRes,
  UpdateVendorSettingsByLocationReq,
  UpdateVendorSettingsByLocationRes,
  UpdateVendorContactReq,
  UpdateVendorContactRes,
  UpdateVendorInformationReq,
  UpdateVendorInformationRes,
  UpdateVendorIntegrationReq,
  UpdateVendorIntegrationRes,
  UpdateVendorIsActiveReq,
  UpdateVendorIsActiveRes,
  UpdateVendorNotesReq,
  UpdateVendorNotesRes,
  UpdateVendorOrderReceiveSettingsReq,
  UpdateVendorOrderReceiveSettingsRes,
  UpdateVendorOrderSettingsReq,
  UpdateVendorOrderSettingsRes,
  UpsertVendorBankAccountReq,
  UpsertVendorBankAccountRes,
} from "gen/api/vendors/service_pb"
import { VendorsServiceClient } from "gen/api/vendors/service_pb.client"
import { UUID } from "shared-ts/classes/uuid"

import { apiSlice, transport } from "./apiSlice"

const c = new VendorsServiceClient(transport)

const vendorApiSlice = apiSlice
  .enhanceEndpoints({ addTagTypes: ["Vendors"] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getVendor: builder.query<GetVendorRes, GetVendorReq>({
        query: (r) => c.getVendor(r),
        providesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.fromPB(request.id!).toString() },
        ],
      }),
      getVendorByIntegration: builder.query<
        GetVendorByIntegrationRes,
        GetVendorByIntegrationReq
      >({
        query: (r) => c.getVendorByIntegration(r),
        providesTags: ["Vendors"],
      }),
      listVendorsForCompany: builder.query<
        ListVendorsForCompanyRes,
        ListVendorsForCompanyReq
      >({
        query: (r) => c.listVendorsForCompany(r),
        providesTags: ["Vendors"],
      }),
      createVendorContact: builder.mutation<
        CreateVendorContactRes,
        CreateVendorContactReq
      >({
        query: (r) => c.createVendorContact(r),
        invalidatesTags: (_response, _error, request) => [
          {
            type: "Vendors",
            id: UUID.fromPB(request.vendorId!).toString(),
          },
        ],
      }),
      createVendor: builder.mutation<CreateVendorRes, CreateVendorReq>({
        query: (r) => c.createVendor(r),
        invalidatesTags: ["Vendors"],
      }),
      updateVendorInformation: builder.mutation<
        UpdateVendorInformationRes,
        UpdateVendorInformationReq
      >({
        query: (r) => c.updateVendorInformation(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.fromPB(request.id!).toString() },
        ],
      }),
      updateVendorIsActive: builder.mutation<
        UpdateVendorIsActiveRes,
        UpdateVendorIsActiveReq
      >({
        query: (r) => c.updateVendorIsActive(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.fromPB(request.id!).toString() },
        ],
      }),
      updateVendorNotes: builder.mutation<
        UpdateVendorNotesRes,
        UpdateVendorNotesReq
      >({
        query: (r) => c.updateVendorNotes(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.fromPB(request.id!).toString() },
        ],
      }),
      updateVendorContact: builder.mutation<
        UpdateVendorContactRes,
        UpdateVendorContactReq
      >({
        query: (r) => c.updateVendorContact(r),
        invalidatesTags: (_response, _error, request) => [
          {
            type: "Vendors",
            id: UUID.fromPB(request.vendorId!).toString(),
          },
        ],
      }),
      deactivateVendorContact: builder.mutation<
        DeactivateVendorContactRes,
        DeactivateVendorContactReq
      >({
        query: (r) => c.deactivateVendorContact(r),
        invalidatesTags: (_response, _error, request) => [
          {
            type: "Vendors",
            id: UUID.fromPB(request.vendorId!).toString(),
          },
        ],
      }),
      saveVendorBankPaymentMethod: builder.mutation<
        SaveVendorBankPaymentMethodRes,
        SaveVendorBankPaymentMethodReq
      >({
        query: (r) => c.saveVendorBankPaymentMethod(r),
        invalidatesTags: (_response, _error, request) =>
          request.vendorId
            ? [
                {
                  type: "Vendors",
                  id: UUID.fromPB(request.vendorId).toString(),
                },
              ]
            : [],
      }),
      deleteVendorBankPaymentMethod: builder.mutation<
        DeleteVendorBankPaymentMethodRes,
        DeleteVendorBankPaymentMethodReq
      >({
        query: (r) => c.deleteVendorBankPaymentMethod(r),
        invalidatesTags: (_response, _error, request) =>
          request.vendorId
            ? [
                {
                  type: "Vendors",
                  id: UUID.fromPB(request.vendorId).toString(),
                },
              ]
            : [],
      }),
      createVendorWithBankAccount: builder.mutation<
        CreateVendorWithBankAccountRes,
        CreateVendorWithBankAccountReq
      >({
        query: (r) => c.createVendorWithBankAccount(r),
        invalidatesTags: ["Vendors"],
      }),
      updateVendorOrderSettings: builder.mutation<
        UpdateVendorOrderSettingsRes,
        UpdateVendorOrderSettingsReq
      >({
        query: (r) => c.updateVendorOrderSettings(r),
        invalidatesTags: (_res, _err, req) =>
          req.vendorId
            ? [{ type: "Vendors", id: UUID.fromPB(req.vendorId).toString() }]
            : [],
      }),
      updateVendorOrderReceiveSettings: builder.mutation<
        UpdateVendorOrderReceiveSettingsRes,
        UpdateVendorOrderReceiveSettingsReq
      >({
        query: (r) => c.updateVendorOrderReceiveSettings(r),
        invalidatesTags: (_res, _err, req) =>
          req.vendorId
            ? [{ type: "Vendors", id: UUID.fromPB(req.vendorId).toString() }]
            : [],
      }),
      upsertVendorBankAccount: builder.mutation<
        UpsertVendorBankAccountRes,
        UpsertVendorBankAccountReq
      >({
        query: (r) => c.upsertVendorBankAccount(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.fromPB(request.vendorId!).toString() },
        ],
      }),
      UpdateVendorAccountsByLocation: builder.mutation<
        UpdateVendorAccountsByLocationRes,
        UpdateVendorAccountsByLocationReq
      >({
        query: (r) => c.updateVendorAccountsByLocation(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.fromPB(request.id!).toString() },
        ],
      }),
      updateVendorLocationSettings: builder.mutation<
        UpdateVendorLocationSettingsRes,
        UpdateVendorLocationSettingsReq
      >({
        query: (r) => c.updateVendorLocationSettings(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.fromPB(request.id!).toString() },
        ],
      }),
      updateVendorSettingsByLocation: builder.mutation<
        UpdateVendorSettingsByLocationRes,
        UpdateVendorSettingsByLocationReq
      >({
        query: (r) => c.updateVendorSettingsByLocation(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.idString(request.id) },
        ],
      }),
      bulkUpsertVendors: builder.mutation<
        BulkUpsertVendorsRes,
        BulkUpsertVendorsReq
      >({
        query: (r) => c.bulkUpsertVendors(r),
      }),
      duplicateVendor: builder.mutation<DuplicateVendorRes, DuplicateVendorReq>(
        {
          query: (r) => c.duplicateVendor(r),
        }
      ),
      listVendorIntegrationOptions: builder.query<
        ListVendorIntegrationOptionsRes,
        ListVendorIntegrationOptionsReq
      >({
        query: (r) => c.listVendorIntegrationOptions(r),
      }),
      updateVendorIntegration: builder.mutation<
        UpdateVendorIntegrationRes,
        UpdateVendorIntegrationReq
      >({
        query: (r) => c.updateVendorIntegration(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "Vendors", id: UUID.fromPB(request.vendorId!).toString() },
        ],
      }),
      listCatalogVendorIntegrationOptions: builder.query<
        ListCatalogVendorIntegrationOptionsRes,
        ListCatalogVendorIntegrationOptionsReq
      >({
        query: (r) => c.listCatalogVendorIntegrationOptions(r),
      }),
      listVendorLocationSettingsOptions: builder.query<
        ListVendorLocationSettingsOptionsRes,
        ListVendorLocationSettingsOptionsReq
      >({
        query: (r) => c.listVendorLocationSettingsOptions(r),
        providesTags: ["Vendors"],
      }),
      assignVendorSkus: builder.mutation<
        AssignVendorSkusRes,
        AssignVendorSkusReq
      >({
        query: (r) => c.assignVendorSkus(r),
      }),
    }),
  })

export const {
  useGetVendorQuery,
  useLazyGetVendorQuery,
  useGetVendorByIntegrationQuery,
  useLazyGetVendorByIntegrationQuery,
  useListVendorsForCompanyQuery,
  useLazyListVendorsForCompanyQuery,
  useCreateVendorMutation,
  useCreateVendorContactMutation,
  useUpdateVendorInformationMutation,
  useUpdateVendorIsActiveMutation,
  useUpdateVendorNotesMutation,
  useUpdateVendorContactMutation,
  useUpdateVendorOrderSettingsMutation,
  useUpdateVendorOrderReceiveSettingsMutation,
  useDeactivateVendorContactMutation,
  useSaveVendorBankPaymentMethodMutation,
  useDeleteVendorBankPaymentMethodMutation,
  useCreateVendorWithBankAccountMutation,
  useUpsertVendorBankAccountMutation,
  useUpdateVendorAccountsByLocationMutation,
  useUpdateVendorLocationSettingsMutation,
  useUpdateVendorSettingsByLocationMutation,
  useBulkUpsertVendorsMutation,
  useDuplicateVendorMutation,
  useListVendorIntegrationOptionsQuery,
  useListCatalogVendorIntegrationOptionsQuery,
  useListVendorLocationSettingsOptionsQuery,
  useUpdateVendorIntegrationMutation,
  useAssignVendorSkusMutation,
} = vendorApiSlice

export function useGetActiveVendorCountQuery() {
  return useQuery({
    queryKey: ["active-vendor-count"],
    queryFn: async () => {
      const { response } = await c.getActiveVendorCount({})
      return response
    },
  })
}
